import {
  ApolloClient,
  ApolloLink,
  HttpLink,
  InMemoryCache,
} from "@apollo/client";
import { onError } from "@apollo/client/link/error";

import { createGraphqlNetworkObservabilityLinks } from "./graphqlNetworkObservability";
import { reportBasecampError } from "./sentry";

const cache = new InMemoryCache();
const LEGACY_SESSION_STORAGE_KEYS = [
  "ths-basecamp-token",
  "basecamp-apollo-cache",
] as const;
const NON_REPORTABLE_GRAPHQL_CODES = new Set([
  "BAD_USER_INPUT",
  "CONFLICT",
  "FORBIDDEN",
  "GRAPHQL_VALIDATION_FAILED",
  "NOT_FOUND",
  "UNAUTHENTICATED",
]);

export async function createApolloClient(): Promise<ApolloClient<unknown>> {
  // Reservation details and door codes stay in memory. Only the guest session
  // credential is durable; discard obsolete assignment credentials and caches.
  if (typeof window !== "undefined") {
    for (const key of LEGACY_SESSION_STORAGE_KEYS) {
      try {
        window.localStorage.removeItem(key);
      } catch {
        // Storage can be unavailable in hardened/private browser contexts.
      }
    }
  }

  const graphqlUrl =
    import.meta.env.VITE_APP_GRAPHQL_API_URL ??
    "http://localhost:4000/client-graphql";
  const basecampUrl = `${new URL(graphqlUrl).origin}/basecamp-graphql`;

  const errorLink = onError(({ graphQLErrors, operation }) => {
    for (const error of graphQLErrors ?? []) {
      const code =
        typeof error.extensions?.code === "string"
          ? error.extensions.code
          : undefined;

      if (code && NON_REPORTABLE_GRAPHQL_CODES.has(code)) continue;

      reportBasecampError(error, {
        feature: "apollo",
        action: "graphql-error",
        operationName: operation.operationName,
        code,
      });
    }
  });

  const { observabilityLink, attemptLink } =
    createGraphqlNetworkObservabilityLinks();

  return new ApolloClient({
    link: ApolloLink.from([
      observabilityLink,
      errorLink,
      attemptLink,
      new HttpLink({ uri: basecampUrl }),
    ]),
    cache,
    defaultOptions: {
      watchQuery: { fetchPolicy: "cache-and-network", errorPolicy: "all" },
      query: { fetchPolicy: "cache-first", errorPolicy: "all" },
    },
  });
}
